/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import { useState, useEffect, useMemo } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Table as BTable } from 'react-bootstrap';
import Container from 'react-bootstrap/Container';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp, faFilter, faWrench } from '@fortawesome/free-solid-svg-icons';
import { Badge } from 'react-bootstrap';
import { Button, Row, Col, Stack } from 'react-bootstrap';
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom';
import { Probe } from '../data';

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  ColumnFiltersState,
} from '@tanstack/react-table'
import Form from 'react-bootstrap/Form';

let JSONSearch = function(row, columnId, filterValue) {
    if (row.getValue(columnId) === null || row.getValue(columnId) === undefined) {
        return false;
    }
    const value = JSON.stringify(row.getValue(columnId)).toLowerCase();
    return value.includes(filterValue.toLowerCase());
}

function ProbeTable({ rowSelection, setRowSelection, data, doSearch, tableContainerStyle = null, columnVis = null }) {
    const [ globalFilter, setGlobalFilter ] = useState<string>("");
    if (columnVis === null) {
        columnVis = {
            probe_type: true,
            probe_id: true,
            f_vendor: true,
            f_product: true,
            finger_id: true,
            run_type: true,
            run_id: true,
            run_ts: true,
            passed: true,
            detail: false,
        };
    }
    const navigate = useNavigate();

    const columns: ColumnDef<Probe>[] = [
            {
                id: 'probe_id',
                header: 'ID',
                accessorKey: 'probe_id',
                filterFn: 'includesString',
            },
            {
                id: 'probe_type',
                header: 'Type',
                accessorKey: 'probe_type',
                filterFn: 'includesString',
            },
            {
                id: 'f_vendor',
                header: 'Vendor',
                accessorKey: 'f_vendor',
                filterFn: 'includesString',
            },
            {
                id: 'f_product',
                header: 'Product',
                accessorKey: 'f_product',
                filterFn: 'includesString',
            },
            {
                id: 'finger_id',
                header: 'Id',
                accessorKey: 'finger_id',
                filterFn: 'equals',
            },
            {
                id: 'run_type',
                header: 'Run Type',
                accessorKey: 'run_type',
                filterFn: 'includesString',
            },
            {
                id: 'run_id',
                header: 'Run',
                accessorKey: 'run_id',
                filterFn: 'equals',
            },
            {
                id: 'run_ts',
                header: 'Time',
                accessorKey: 'run_ts',
                filterFn: 'includesString',
                cell: ({ getValue }) => {
                    const ts = getValue() as number;
                    if (!ts) return <div>—</div>;
                    return <div>{dayjs(ts * 1000).format('YYYY-MM-DD HH:mm:ss')}</div>
                },
            },
            {
                id: 'passed',
                header: 'Pass',
                accessorKey: 'passed',
                cell: ({ getValue }) => {
                    if (getValue() == 1) {
                        return <Badge pill bg="success">Pass</Badge>
                    } else {
                        return <Badge pill bg="danger">Fail</Badge>
                    }
                },
            },
    ];

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        enableGlobalFilter: true,
        enableMultiRowSelection: false,
        onRowSelectionChange: setRowSelection,
        getRowId: (row : any) => row['fid'],
        state: {
            rowSelection,
            columnVisibility: columnVis,
            globalFilter,
        },
    })

    const rowStyle = tableContainerStyle ? tableContainerStyle :
            {   height: 'calc(100vh - 48px)',
                display: 'block',
                overflowY: 'auto',
                overflowX: 'hidden' 
            };

    return (
        <Container style={rowStyle}>
            { doSearch && (     
            <Row className="pb-2 mb-2 border-bottom">
                <Stack direction="horizontal" gap={3} className="my-4">
                    <input
                        value={globalFilter ?? ''}
                        onChange={e => {
                            setGlobalFilter(e.target.value);
                        }}
                        className="p-2 font-lg shadow border border-block"
                        placeholder="Search..."
                    />
                </Stack>
            </Row>
            )}
            <Row>
                <BTable hover>
                <thead>{table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>{headerGroup.headers.map(header => (
                        <th style={{ position: 'sticky',
                                    top: 0,
                                    background: '#fff',
                                    zIndex: 1 }}
                            key={header.id} colSpan={header.colSpan}
                        >
                            <div onClick={header.column.getToggleSortingHandler()}>
                                {header.isPlaceholder ? null : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                ) }
                                {header.column.getIsSorted() ? (
                                    <SortIcon>
                                        <FontAwesomeIcon icon={header.column.getIsSorted() === 'desc' ? faCaretDown : faCaretUp} />
                                    </SortIcon>
                                ) : null }
                            </div>
                        </th>
                    ))}</tr>
                ))}</thead>
                <tbody>
                {table.getRowModel().rows.map(row => (
                    <tr key={row.id}
                        className={row.getIsSelected() ? 'table-info' : undefined}
                    >
                        {row.getVisibleCells().map(cell => cell.column.id === 'select-col' ? (
                            <td onClick={row.getToggleSelectedHandler()} key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                        ) : (
                            <td onClick={() => navigate(`/probe/${row.getValue('probe_id')}`)} key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                        ))}
                    </tr>
                ))}</tbody>
                <tfoot>{table.getFooterGroups().map(footerGroup => (
                    <tr key={footerGroup.id}>{footerGroup.headers.map(header => (
                        <th key={header.id} colSpan={header.colSpan}>{header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.footer,
                                header.getContext()
                        )}</th>
                    ))}</tr>
                ))}</tfoot>
            </BTable>
        </Row>
    </Container>
  )
}

//Styles
const SortIcon = styled.span`
    margin-left: 5px;
`

export default ProbeTable;
//                <tr onClick={row.getToggleSelectedHandler()} key={row.id}